import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './i18n'
import App from './App.tsx'
import { registerSW } from 'virtual:pwa-register'
import { Capacitor } from '@capacitor/core'

import * as Sentry from '@sentry/react'

if (typeof window !== 'undefined') {
  (window as any).Sentry = Sentry
}

const isProd = typeof window !== 'undefined' && (window.location.hostname === 'estipana.com' || window.location.hostname === 'www.estipana.com')
const sentryDsn = import.meta.env.VITE_SENTRY_DSN || 'https://3df1a758545a8119e41ec34523fdd09a@o4511823806726144.ingest.us.sentry.io/4511824946462720'
if (sentryDsn) {
  try {
    Sentry.init({
      dsn: sentryDsn,
      environment: isProd ? 'production' : 'staging',
      integrations: [
        Sentry.browserTracingIntegration(),
      ],
      tracesSampleRate: 0.1,
      tracePropagationTargets: ['localhost', 'staging.estipana.com', 'estipana.com', /^https:\/\/.*\.estipana\.com\/api/],
    })
  } catch (e) {
    // Graceful fallback if Sentry DSN is unconfigured
  }
}

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    sessionStorage.setItem('pwa_update_success', 'true')
  })
}

// Support relative fetches on Capacitor by prefixing the base API URL
const originalFetch = window.fetch
window.fetch = function (input, init) {
  // Simulate offline mode by rejecting all outgoing backend API fetches
  if (typeof localStorage !== 'undefined' && localStorage.getItem('estipana_force_offline') === 'true') {
    const isApi = (
      (typeof input === 'string' && input.includes('/api/')) ||
      (input instanceof URL && input.pathname.startsWith('/api/')) ||
      (input instanceof Request && (input.url.includes('/api/') || new URL(input.url, window.location.origin).pathname.startsWith('/api/')))
    );
    if (isApi) {
      return Promise.reject(new TypeError('Failed to fetch (Force Offline Mode)'));
    }
  }

  const defaultNativeApi = 'https://staging.estipana.com'
  const apiBase = import.meta.env.VITE_API_URL || (Capacitor.isNativePlatform() ? defaultNativeApi : '')
  if (!apiBase) {
    return originalFetch.call(this, input, init)
  }

  try {
    let urlStr = ''
    if (typeof input === 'string') {
      urlStr = input
    } else if (input instanceof URL) {
      urlStr = input.toString()
    } else if (input instanceof Request) {
      urlStr = input.url
    }

    if (urlStr) {
      const isRelativeApi = urlStr.startsWith('/api/')
      const parsedUrl = new URL(urlStr, window.location.origin)
      if (isRelativeApi || parsedUrl.pathname.startsWith('/api/')) {
        const fullTargetUrl = urlStr.startsWith('http') ? urlStr : `${apiBase}${parsedUrl.pathname}${parsedUrl.search}`
        
        if (input instanceof Request) {
          const reqMethod = input.method || 'GET'
          const reqOptions: RequestInit = {
            method: reqMethod,
            headers: input.headers,
            credentials: 'include',
            mode: input.mode,
            cache: input.cache,
            redirect: input.redirect,
            referrer: input.referrer,
            signal: input.signal
          }
          if (reqMethod !== 'GET' && reqMethod !== 'HEAD') {
            reqOptions.body = input.body
          }
          const newReq = new Request(fullTargetUrl, reqOptions)
          return originalFetch.call(this, newReq, init)
        }
        
        return originalFetch.call(this, fullTargetUrl, { credentials: 'include', ...(init || {}) })
      }
    }
  } catch (err) {
    console.error('Fetch interceptor error:', err)
  }

  return originalFetch.call(this, input, init)
}

registerSW({ immediate: true })

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
